import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ordersAPI, subscriptionsAPI } from '../services/api';

const money = (n) => `£${Number(n).toFixed(2)}`;
const date = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '—');

const STAGES = [
  { key: 'pending', label: 'Booked' },
  { key: 'picked_up', label: 'Picked up' },
  { key: 'processing', label: 'Cleaning' },
  { key: 'ready', label: 'Ready' },
  { key: 'delivered', label: 'Delivered' }
];

function Tracker({ status }) {
  if (status === 'cancelled') {
    return <span className="status-badge status-cancelled">Cancelled</span>;
  }
  const ci = STAGES.findIndex((s) => s.key === status);
  const delivered = status === 'delivered';
  return (
    <div className="tracker">
      {STAGES.map((s, i) => {
        const cls = delivered || i < ci ? 'done' : i === ci ? 'current' : '';
        return (
          <div className={`tracker-step ${cls}`} key={s.key}>
            <div className="tracker-dot">{delivered || i < ci ? '✓' : i + 1}</div>
            <div className="tracker-label">{s.label}</div>
          </div>
        );
      })}
    </div>
  );
}

const MyOrders = () => {
  const [orders, setOrders] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [activeTab, setActiveTab] = useState('orders');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchData = async () => {
    try {
      const [o, s] = await Promise.all([ordersAPI.getMyOrders(), subscriptionsAPI.getMySubscriptions()]);
      setOrders(o.data);
      setSubscriptions(s.data);
    } catch {
      setError('Failed to load your orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const cancelSub = async (id) => {
    if (!window.confirm('Cancel this subscription?')) return;
    try { await subscriptionsAPI.cancel(id); fetchData(); }
    catch { alert('Failed to cancel subscription'); }
  };

  if (loading) return <div className="loading">Loading your orders…</div>;

  return (
    <div className="container">
      <h1 style={{ marginBottom: '0.3rem' }}>My orders &amp; subscriptions</h1>
      <p style={{ color: 'var(--ink-soft)', marginBottom: '1.5rem' }}>Track your laundry and manage your plans.</p>

      {error && <div className="error-message">{error}</div>}

      <div className="admin-tabs">
        <button className={`btn ${activeTab === 'orders' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setActiveTab('orders')}>Dry cleaning</button>
        <button className={`btn ${activeTab === 'subscriptions' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setActiveTab('subscriptions')}>Subscriptions</button>
      </div>

      {activeTab === 'orders' && (
        orders.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
            <h3>No orders yet</h3>
            <p>Ready when you are.</p>
            <Link to="/book-alacarte" className="btn btn-accent">Book dry cleaning</Link>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '1.2rem' }}>
            {orders.map((o) => (
              <div className="card" key={o.id}>
                <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1.2rem' }}>
                  <div>
                    <strong>Order #{o.id}</strong>
                    <span style={{ color: 'var(--ink-soft)', marginLeft: '0.6rem', fontSize: '0.9rem' }}>Booked {date(o.created_at)}</span>
                  </div>
                  <div style={{ fontFamily: "'Poppins',sans-serif", fontWeight: 700, color: 'var(--primary-dark)' }}>{money(o.total_price)}</div>
                </div>

                <Tracker status={o.status} />

                <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem', marginTop: '1.2rem', fontSize: '0.9rem', color: 'var(--ink-soft)' }}>
                  <span>🚐 Collection: <strong style={{ color: 'var(--ink)' }}>{date(o.pickup_date)}</strong></span>
                  <span>📦 Delivery: <strong style={{ color: 'var(--ink)' }}>{date(o.delivery_date)}</strong></span>
                  <span>{Array.isArray(o.items) ? o.items.reduce((s, i) => s + i.quantity, 0) : 0} item(s)</span>
                </div>
                {Array.isArray(o.items) && o.items.length > 0 && (
                  <div style={{ marginTop: '0.6rem', fontSize: '0.85rem', color: 'var(--ink-soft)' }}>
                    {o.items.map((i) => `${i.name} ×${i.quantity}`).join(' · ')}
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      )}

      {activeTab === 'subscriptions' && (
        subscriptions.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
            <h3>No subscriptions</h3>
            <p>Set up regular collections and never think about laundry again.</p>
            <Link to="/book-subscription" className="btn btn-accent">Start a subscription</Link>
          </div>
        ) : (
          <div className="cards-grid">
            {subscriptions.map((sub) => (
              <div key={sub.id} className="card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                  <h3>{sub.subscription_type === 'laundry' ? 'Laundry' : 'Shirts & Trousers'}</h3>
                  <span className={`status-badge status-${sub.status}`}>{sub.status}</span>
                </div>
                <div style={{ marginTop: '0.8rem' }}>
                  <p style={{ marginBottom: '0.3rem' }}>{sub.description}</p>
                  <p className="price-small">{money(sub.price)}/{sub.frequency === 'weekly' ? 'week' : 'fortnight'}</p>
                  {sub.next_pickup_date && <p style={{ marginTop: '0.5rem', fontSize: '0.9rem' }}>Next collection: <strong>{date(sub.next_pickup_date)}</strong></p>}
                </div>
                {sub.status === 'active' && (
                  <button onClick={() => cancelSub(sub.id)} className="btn btn-outline btn-block" style={{ marginTop: '1rem', color: 'var(--error-color)', borderColor: 'var(--error-color)' }}>
                    Cancel subscription
                  </button>
                )}
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
};

export default MyOrders;
